import React from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent,
  DialogActions, 
  TextField, 
  Box, 
  Button,
  Alert,
  Typography,
  Chip
} from "@mui/material";
import TankIcon from '@mui/icons-material/Water';
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from "axios";
import { useNavigate } from "react-router-dom";

// 🚀 簡潔化：サービスモード対応製品情報編集ダイアログ
const ProductEditDialog = ({ 
  open, 
  onClose, 
  clientProduct, 
  selectedClientProductId,
  onUpdate,
  serviceMode = 'PREDICTION_AUTO'
}) => {
  const navigate = useNavigate();
  const [editedProduct, setEditedProduct] = React.useState({
    totalCapacity: "",
    initialConsumptionRate: "",
    thresholdQuantity: ""
  });
  
  const [productErrors, setProductErrors] = React.useState({
    totalCapacity: "",
    initialConsumptionRate: "",
    thresholdQuantity: ""
  });

  const MAX_NUMBER = 200000;

  // 🚀 サービスモード別設定（簡潔化）
  const modeConfigs = {
    'PRICING_ONLY': {
      icon: <MonetizationOnIcon color="warning" />,
      label: '基本モード',
      availableFields: [],
      showDialog: false
    },
    'TANK_MANUAL': {
      icon: <TankIcon color="primary" />,
      label: 'タンク管理',
      availableFields: ['totalCapacity'], // 🚀 thresholdQuantityを除外
      showDialog: true
    },
    'PREDICTION_ONLY': {
      icon: <TimelineIcon color="info" />,
      label: '予測機能',
      availableFields: ['initialConsumptionRate'],
      showDialog: true
    },
    'PREDICTION_AUTO': {
      icon: <AutoAwesomeIcon color="success" />,
      label: 'フル機能',
      availableFields: ['totalCapacity', 'initialConsumptionRate', 'thresholdQuantity'],
      showDialog: true
    }
  };

  const config = modeConfigs[serviceMode] || modeConfigs['PRICING_ONLY'];

  // 🚀 フィールド設定（簡潔化）
  const fieldConfigs = {
    totalCapacity: {
      label: 'タンク容量 (L)',
      placeholder: '1000'
    },
    initialConsumptionRate: {
      label: 'ひと月あたりの消費量 (L)',
      placeholder: '100'
    },
    thresholdQuantity: {
      label: '補充目安 (L)',
      placeholder: '200'
    }
  };

  // 初期化
  React.useEffect(() => {
    if (open && clientProduct) {
      setEditedProduct({
        totalCapacity: clientProduct.totalCapacity?.toString() || "",
        initialConsumptionRate: clientProduct.initialConsumptionRate?.toString() || "",
        thresholdQuantity: clientProduct.thresholdQuantity?.toString() || ""
      });
      setProductErrors({
        totalCapacity: "",
        initialConsumptionRate: "",
        thresholdQuantity: ""
      });
    }
  }, [open, clientProduct]);

  // バリデーション（簡潔化）
  const handleProductInputChange = (field, value) => {
    if (!config.availableFields.includes(field)) return;

    const newErrors = { ...productErrors };
    const updated = { ...editedProduct, [field]: value };
    setEditedProduct(updated);

    if (field === "totalCapacity") {
      if (value === "") {
        newErrors.totalCapacity = "";
      } else if (!/^[0-9]+$/.test(value)) {
        newErrors.totalCapacity = "数値を入力してください";
      } else if (Number(value) < 1 || Number(value) > MAX_NUMBER) {
        newErrors.totalCapacity = "1〜20万の範囲で入力してください";
      } else {
        newErrors.totalCapacity = "";
      }
    } else if (field === "initialConsumptionRate") {
      if (value === "" || /^[0-9]+$/.test(value)) {
        if (value && Number(value) > MAX_NUMBER) {
          newErrors.initialConsumptionRate = "20万以下で入力してください";
        } else {
          newErrors.initialConsumptionRate = "";
        }
      } else {
        newErrors.initialConsumptionRate = "数値を入力してください";
      }
    } else if (field === "thresholdQuantity") {
      if (value === "" || /^[0-9]+$/.test(value)) {
        if (value && Number(value) > MAX_NUMBER) {
          newErrors.thresholdQuantity = "20万以下で入力してください";
        } else if (value && updated.totalCapacity && Number(value) > Number(updated.totalCapacity)) {
          newErrors.thresholdQuantity = `容量（${updated.totalCapacity}L）以下で設定してください`;
        } else {
          newErrors.thresholdQuantity = "";
        }
      } else {
        newErrors.thresholdQuantity = "数値を入力してください";
      }
    }
    
    setProductErrors(newErrors);
  };

  const hasProductError = Object.values(productErrors).some((v) => v);

  // 🚀 修正：更新処理 - 空文字をnullに変換
  const handleSubmit = async () => {
    const token = sessionStorage.getItem("token");
    
    try {
      const submitData = {};
      config.availableFields.forEach(field => {
        const value = editedProduct[field];
        // 🚀 修正：空文字またはundefinedの場合はnullを送信
        if (value === "" || value === undefined || value === null) {
          submitData[field] = null;
        } else {
          submitData[field] = Number(value);
        }
      });

      await axios.put(
        `/api/client-products/${selectedClientProductId}`,
        submitData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert("製品情報を更新しました");
      
      // 🚀 修正：ダイアログを閉じて顧客詳細画面に戻る
      onClose();
      
      // 🚀 修正：データ更新のみ行う（別のダイアログは開かない）
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      if (err.response?.status === 404) {
        alert("この顧客は削除されています。一覧に戻ります。");
        navigate("/clientList");
      } else {
        alert("更新に失敗しました");
      }
    }
  };

  // 🚀 基本モードの場合（簡潔化）
  if (!config.showDialog) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {config.icon}
          <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
            {config.label}
          </Typography>
          <Alert severity="info">
            機能設定から追加機能を有効にしてください
          </Alert>
        </DialogContent>
        {/* 🚀 統一されたボタンスタイル */}
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
          <Button 
            variant="text"
            onClick={onClose}
            color="inherit"
            size="medium"
            sx={{ minWidth: 100 }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {config.icon}
          <Typography variant="h6">製品設定</Typography>
        </Box>
      </DialogTitle>

      {/* 🚀 修正：上部の余白を確保してラベル切れを防止 */}
      <DialogContent sx={{ pt: 3 }}>
        {/* 🚀 利用可能フィールドのみ表示（説明文なし） */}
        {config.availableFields.includes('totalCapacity') && (
          <TextField
            label={fieldConfigs.totalCapacity.label}
            type="text"
            fullWidth
            value={editedProduct.totalCapacity}
            onChange={(e) => handleProductInputChange("totalCapacity", e.target.value)}
            error={!!productErrors.totalCapacity}
            helperText={productErrors.totalCapacity}
            sx={{ mb: 2 }}
            placeholder={fieldConfigs.totalCapacity.placeholder}
            InputLabelProps={{ shrink: true }} // 🚀 修正：ラベルを常に表示
          />
        )}

        {config.availableFields.includes('initialConsumptionRate') && (
          <TextField
            label={fieldConfigs.initialConsumptionRate.label}
            type="text"
            fullWidth
            value={editedProduct.initialConsumptionRate}
            onChange={(e) => handleProductInputChange("initialConsumptionRate", e.target.value)}
            error={!!productErrors.initialConsumptionRate}
            helperText={productErrors.initialConsumptionRate}
            sx={{ mb: 2 }}
            placeholder={fieldConfigs.initialConsumptionRate.placeholder}
            InputLabelProps={{ shrink: true }} // 🚀 修正：ラベルを常に表示
          />
        )}

        {config.availableFields.includes('thresholdQuantity') && (
          <TextField
            label={fieldConfigs.thresholdQuantity.label}
            type="text"
            fullWidth
            value={editedProduct.thresholdQuantity}
            onChange={(e) => handleProductInputChange("thresholdQuantity", e.target.value)}
            error={!!productErrors.thresholdQuantity}
            helperText={productErrors.thresholdQuantity}
            sx={{ mb: 2 }}
            placeholder={fieldConfigs.thresholdQuantity.placeholder}
            InputLabelProps={{ shrink: true }} // 🚀 修正：ラベルを常に表示
          />
        )}
      </DialogContent>

      {/* 🚀 統一されたボタンスタイル */}
      <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={hasProductError}
          size="large"
          fullWidth
          sx={{ order: 1 }}
        >
          保存
        </Button>
        
        <Button 
          variant="text"
          onClick={onClose}
          color="inherit"
          size="medium"
          sx={{ order: 2, minWidth: 100 }}
        >
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEditDialog;